import { useState, useEffect, useCallback, useContext } from "react";
import { debounce } from "lodash";
import axios from "axios";
import {
  Avatar,
  Box,
  IconButton,
  Skeleton,
  TextField,
} from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";

import TicketItem from "./TicketItem";
import InfiniteScroll from "react-infinite-scroll-component";
import Ticket from "../@type";
import CreateNewTicket from "../CreateNewTicket";

interface IProps {
  sx?: any;
  setActiveTicket: (ticket: any) => void;
  activeTicketId?: number;
  searchStyles?: any;
  isMobile?: boolean;
  close: () => void;
}

export default function TicketsList({
  sx,
  setActiveTicket,
  activeTicketId,
  searchStyles,
  close,
  isMobile
}: IProps) {
  const [tickets, setTickets] = useState<any>({
    data: [],
    current_page: 1,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [createNewTicket, setCreateNewTicket] = useState(false);

  const getTickets = async (isNextPage?: boolean) => {
    if (isNextPage) {
      setTickets({
        ...tickets,
        current_page: tickets.current_page++,
      });
    } else {
      setLoading(true);
    }

    const { data } = await axios.get("/support-tickets/get-list", {
      params: isNextPage
        ? {
          page: tickets.current_page,
        }
        : {},
    });
    setTickets({
      ...tickets,
      data: [...tickets.data, ...data.data.data],
      total_pages: data.data.last_page,
      current_page: data.data.current_page,
    });

    setLoading(false);
  };

  const searchTicket = useCallback(
    debounce(async (inputValue) => {
      const { data } = await axios.get("/support-tickets/get-list", {
        params: { search_key: inputValue },
      });

      setTickets({
        ...tickets,
        data: data.data.data,
        total_pages: data.data.last_page,
        current_page: data.data.current_page,
      });
    }, 500),
    []
  );

  useEffect(() => {
    getTickets();
  }, []);

  const scrollableTarget = isMobile ? "tickets-list-mobile" : "tickets-list";

  return (
    <Box>
      <CreateNewTicket
        updateTickets={(object: any) => {
          setTickets({
            ...tickets,
            data: [object, ...tickets.data],
          });
        }}
        isOpen={createNewTicket}
        handleClose={() => setCreateNewTicket(false)}
      />

      <Box
        sx={{
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          p: 1,
          pb: 0,
          ...searchStyles,
        }}
      >
        <TextField
          sx={{ flex: 1 }}
          onChange={(event: any) => {
            searchTicket(event.target.value);
          }}
          id="standard-basic"
          label="Search"
          variant="standard"
        />
        <IconButton onClick={() => {
          setCreateNewTicket(true);
          close();
        }}>
          <AddBoxIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flexDirection: "column",
          gap: 2,
          overflow: "auto",
          height: "72vh",
          maxHeight: isMobile ? "525px" : "580px",
          ...sx,
        }}
        id={scrollableTarget}
      >
        <InfiniteScroll
          dataLength={tickets.data.length}
          next={() => getTickets(true)}
          pullDownToRefreshThreshold={50}
          hasMore={tickets.current_page < tickets.total_pages}
          loader={
            <Box
              sx={{
                p: 1.5,
              }}
            >
              {Array.from(Array(6).keys()).map((_) => (
                <Box
                  key={_}
                  sx={{
                    transition: "background-color 0.2s",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginTop: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Skeleton variant="circular">
                      <Avatar />
                    </Skeleton>
                    <Skeleton variant="rectangular" width={210} />
                  </Box>
                </Box>
              ))}
            </Box>
          }
          scrollableTarget={scrollableTarget}
        >
          {loading ? (
            <Box
              sx={{
                p: 1.5,
              }}
            >
              {Array.from(Array(10).keys()).map((_) => (
                <Box
                  key={_}
                  sx={{
                    transition: "background-color 0.2s",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginTop: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Skeleton variant="circular">
                      <Avatar />
                    </Skeleton>
                    <Skeleton variant="rectangular" width={210} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            tickets.data.map((item: Ticket, index: number) => (
              <TicketItem
                key={index}
                data={item}
                onClick={() => {
                  setActiveTicket({
                    ...item,
                    unread_ticket: false,
                  });

                  close();
                }}
                isActive={item.ticket_id === activeTicketId}
              />
            ))
          )}
        </InfiniteScroll>
      </Box>
    </Box>
  );
}
